import { useEffect, useState } from 'react';
import { styled } from '@mui/material/styles';
import { useLocation, useNavigate, useSearchParams } from 'react-router';

import { BRAND } from '../../shared/theme/paletteDark';
import { FONTS } from '../../shared/theme/typography';
import { authClient } from './authClient';
import { readPendingEmail } from './authForm';
import { Footer, Note, PageHeading, PrimaryLink, RouterLink } from './authUi';
import { ACCOUNT_PATHS } from './paths';

/**
 * The step between creating an account and using it.
 *
 * Two arrivals share one address. Straight after sign-up there is no token, and
 * the page says where the email went and what to do if it doesn't turn up. From
 * the link in that email there is a token, and the page confirms it.
 */

/** Long enough to stop a nervous double-tap sending three emails. */
const RESEND_COOLDOWN_SECONDS = 30;

export default function VerifyEmailPage() {
  const [params] = useSearchParams();
  const token = params.get('token');
  return token ? <ConfirmLink token={token} /> : <AwaitLink />;
}

const Address = styled('strong')({ color: BRAND.parchment, wordBreak: 'break-all' });

const Secondary = styled('button')({
  appearance: 'none',
  width: '100%',
  minHeight: 48,
  marginTop: 8,
  borderRadius: 999,
  cursor: 'pointer',
  fontFamily: FONTS.body,
  fontSize: 15.5,
  fontWeight: 700,
  color: BRAND.parchment,
  background: 'transparent',
  border: '1px solid rgba(243,238,231,0.45)',
  '&:hover:not(:disabled)': { borderColor: BRAND.parchment },
  '&:disabled': { opacity: 0.55, cursor: 'default' },
});

const Status = styled('p')({
  margin: '10px 0 0',
  minHeight: 20,
  textAlign: 'center',
  fontFamily: FONTS.body,
  fontSize: 13.5,
  color: 'rgba(243,238,231,0.75)',
});

function AwaitLink() {
  const navigate = useNavigate();
  const location = useLocation();
  const email = (location.state as { email?: string } | null)?.email || readPendingEmail();

  // The first email went out a moment ago, so the cooldown starts running now.
  const [wait, setWait] = useState(RESEND_COOLDOWN_SECONDS);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState('');

  useEffect(() => {
    if (wait <= 0) return;
    const tick = setTimeout(() => setWait((s) => s - 1), 1000);
    return () => clearTimeout(tick);
  }, [wait]);

  const resend = async () => {
    setBusy(true);
    setStatus('');
    const result = await authClient.resendVerification(email);
    setBusy(false);
    if (result.ok) {
      setStatus('Sent again. It can take a minute or two to arrive.');
      setWait(RESEND_COOLDOWN_SECONDS);
    } else {
      setStatus(result.message);
    }
  };

  return (
    <>
      <PageHeading
        title="Check your inbox"
        lede={
          email ? (
            <>
              We sent a verification link to <Address>{email}</Address>. Open it to finish
              creating your account — on this device or any other.
            </>
          ) : (
            'We sent you a verification link. Open it to finish creating your account — on this device or any other.'
          )
        }
      />

      <Note style={{ marginTop: 0 }}>
        Not there? It can take a minute. Check your spam or promotions folder too.
      </Note>

      <Secondary type="button" onClick={resend} disabled={busy || wait > 0 || !email}>
        {busy ? 'Sending…' : wait > 0 ? `Resend email in ${wait}s` : 'Resend email'}
      </Secondary>
      <Status role="status">{status}</Status>

      <Note>
        Wrong address?{' '}
        <RouterLink
          to={ACCOUNT_PATHS.signUp}
          onClick={(e) => {
            e.preventDefault();
            navigate(ACCOUNT_PATHS.signUp, { state: { email } });
          }}
        >
          Use a different email
        </RouterLink>
      </Note>

      <Footer>
        Already verified? <RouterLink to={ACCOUNT_PATHS.logIn}>Log in</RouterLink>
      </Footer>
    </>
  );
}

type Confirmation = 'checking' | 'verified' | 'failed';

function ConfirmLink({ token }: { token: string }) {
  const [state, setState] = useState<Confirmation>('checking');

  useEffect(() => {
    let current = true;
    authClient.verifyEmail(token).then((result) => {
      if (current) setState(result.ok ? 'verified' : 'failed');
    });
    return () => {
      current = false;
    };
  }, [token]);

  if (state === 'checking') {
    return <PageHeading title="Verifying your email…" />;
  }

  if (state === 'failed') {
    return (
      <>
        <PageHeading
          title="That link has expired"
          lede="Verification links work for 24 hours. Log in and we'll send you a fresh one."
        />
        <PrimaryLink to={ACCOUNT_PATHS.logIn}>Log in</PrimaryLink>
      </>
    );
  }

  return (
    <>
      <PageHeading
        title="You're verified"
        lede="Your account is ready. Log in on any other device and your schedule and saved events will be waiting there."
      />
      <PrimaryLink to="/">Go to My Climate Week</PrimaryLink>
    </>
  );
}
